import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Button } from '@/Components/ui/button';
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Crown,
  Users,
  Target,
  Search,
  FileCheck,
  Handshake,
  CalendarClock,
  Landmark,
  Megaphone,
  HeartHandshake,
  Cpu,
  Scale,
  BadgeCheck,
  Mic,
  ClipboardCheck,
  UserCheck,
  ShieldCheck,
  MapPin,
  Mail,
  Globe,
  TrendingUp,
  Wallet,
  Briefcase,
  Tag,
  Zap,
  RefreshCcw,
} from 'lucide-react';

/**
 * Fractional Hiring — service showcase page.
 *
 * Positions SearchJob.ai as the place to hire fractional (part-time)
 * senior leaders — CFOs, CMOs, CHROs, VPs and functional heads — for
 * startups and SMEs, with every expert assessed through the SearchJob.ai
 * platform before an introduction. Employer-first, with a single
 * "for experts" band. Rendered in the SearchJob.ai landing theme
 * (cream base, forest-green primary, gradient accents, rounded-3xl
 * cards, dark-ink bands). Helpers are defined locally (mirroring
 * AboutUs.jsx / StaffingServices.jsx).
 *
 * Route: /hirewithus/fractional
 */

const INK = 'hsl(164 33% 7%)';

export default function FractionalHiring() {
  const navigate = useNavigate();

  useSEO({
    title:
      'Fractional Hiring | SearchJob.ai — Hire Fractional CFOs, CMOs, CHROs & Senior Leaders',
    description:
      'Hire experienced fractional leaders — CFOs, CMOs, CHROs, CTOs, VPs and functional heads — for a few days a week instead of full-time payroll. Every expert is assessed through the SearchJob.ai AI platform and reviewed by our team before you meet them.',
    canonical: 'https://searchjob.ai/hirewithus/fractional',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  // Below-hero expert marquee — role + function only (no fabricated
  // employer claims); every card carries the assessed badge.
  const marqueeExperts = [
    { img: '/expert-coo.png', role: 'Fractional COO', fn: 'Operations' },
    { img: '/expert-finhead.png', role: 'Finance Head', fn: 'Finance & Accounts' },
    { img: '/expert-mkthead.png', role: 'Fractional CMO', fn: 'Marketing & Growth' },
    { img: '/expert-hrhead.png', role: 'Fractional CHRO', fn: 'HR & People' },
    { img: '/expert-cto.png', role: 'Fractional CTO', fn: 'Technology & Product' },
    { img: '/expert-saleshead.png', role: 'Sales Head', fn: 'Sales & Revenue' },
  ];

  // Who fractional hiring fits — mirrors how founders actually describe
  // the situation, one accent per card.
  const fitCards = [
    {
      n: '01',
      icon: Target,
      title: 'You need the expertise, not the headcount',
      desc: 'The finance, marketing or people function needs a senior owner — but not forty hours a week of one. A fractional leader gives you the judgment without the payroll.',
      num: 'from-blue-500/40 to-blue-500/5',
      chip: 'bg-blue-500/10 border-blue-500/25 text-blue-600',
    },
    {
      n: '02',
      icon: Wallet,
      title: 'The budget is real, the problem is too',
      desc: 'A defined project, a fundraise, a compliance deadline, a fixed budget. Engage a leader for the months and days per week the work actually needs.',
      num: 'from-amber-500/40 to-amber-500/5',
      chip: 'bg-amber-500/10 border-amber-500/25 text-amber-600',
    },
    {
      n: '03',
      icon: RefreshCcw,
      title: 'Your needs keep changing as you grow',
      desc: 'What you need from a CMO at 10 people is different at 50. Fractional engagements flex up, down, or into a full-time hire as the company evolves.',
      num: 'from-emerald-500/40 to-emerald-500/5',
      chip: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-600',
    },
  ];

  // Role families we place fractionally, with example-role chips.
  const roleFamilies = [
    {
      icon: Crown,
      title: 'CXO & Leadership',
      desc: 'Seasoned operators who have run the function before — engaged for the days per week your stage needs.',
      roles: ['Fractional CFO', 'Fractional CMO', 'Fractional CHRO', 'Fractional CTO', 'Fractional COO'],
      iconGrad: 'from-emerald-500 to-teal-600',
      shadow: 'shadow-emerald-500/30',
      tag: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700',
    },
    {
      icon: Users,
      title: 'VP & Functional Heads',
      desc: 'Hands-on function owners who build the systems, processes and teams a growing company runs on.',
      roles: ['Finance Head', 'HR Head', 'Marketing Head', 'Head of Operations', 'Legal & Compliance Head'],
      iconGrad: 'from-blue-500 to-indigo-600',
      shadow: 'shadow-blue-500/30',
      tag: 'bg-blue-500/10 border-blue-500/20 text-blue-700',
    },
    {
      icon: Sparkles,
      title: 'Specialist Experts',
      desc: 'Deep specialists for the problems that do not need a department — just someone who has solved them before.',
      roles: ['Compensation & Benefits', 'Digital Marketing', 'Sales Enablement', 'Talent Acquisition', 'Data & Analytics'],
      iconGrad: 'from-violet-500 to-purple-600',
      shadow: 'shadow-violet-500/30',
      tag: 'bg-violet-500/10 border-violet-500/20 text-violet-700',
    },
  ];

  // Fractional vs full-time vs consultant — the education table.
  const compareRows = [
    {
      label: 'Commitment',
      fractional: 'A few days a week, for as long as you need',
      fullTime: 'Permanent, full-week payroll',
      consultant: 'Short, scoped project',
    },
    {
      label: 'Cost',
      fractional: 'A fraction of a senior full-time package',
      fullTime: 'Full senior CTC plus ESOPs and benefits',
      consultant: 'Project or hourly fees',
    },
    {
      label: 'Involvement',
      fractional: 'Embedded in your team — owns the function',
      fullTime: 'Embedded, owns the function',
      consultant: 'Advises from outside, then leaves',
    },
    {
      label: 'Flexibility',
      fractional: 'Scale days up, down, or convert to full-time',
      fullTime: 'Fixed — renegotiation or exit',
      consultant: 'Ends with the deliverable',
    },
    {
      label: 'Best for',
      fractional: 'Growing teams that need senior judgment now',
      fullTime: 'Functions with a full-time workload',
      consultant: 'One-off problems and audits',
    },
  ];

  // How the vetting works — every point maps to a real platform feature.
  const vettingPoints = [
    'Structured AI screening on their actual domain — not just a resume read',
    'Functional and role-based assessments on the SearchJob.ai platform',
    'A written evaluation report with evidence you can read before you meet them',
    'Final review by our team — no expert is introduced on machine judgment alone',
  ];

  const steps = [
    {
      n: '01',
      icon: Search,
      title: 'Share the need',
      desc: 'Tell us the function, the outcomes you want, days per week and budget — a short call is enough.',
      num: 'from-blue-500/40 to-blue-500/5',
      chip: 'bg-blue-500/10 border-blue-500/25 text-blue-600',
    },
    {
      n: '02',
      icon: FileCheck,
      title: 'We assess & shortlist',
      desc: 'Matching experts go through AI interviews and functional assessments, reviewed by our team.',
      num: 'from-violet-500/40 to-violet-500/5',
      chip: 'bg-violet-500/10 border-violet-500/25 text-violet-600',
    },
    {
      n: '03',
      icon: Handshake,
      title: 'You meet the experts',
      desc: 'A small, evidence-backed shortlist with assessment reports — you interview and choose.',
      num: 'from-amber-500/40 to-amber-500/5',
      chip: 'bg-amber-500/10 border-amber-500/25 text-amber-600',
    },
    {
      n: '04',
      icon: CalendarClock,
      title: 'Start on your terms',
      desc: 'Agree days per week and duration, start quickly, and flex the engagement as your needs change.',
      num: 'from-emerald-500/40 to-emerald-500/5',
      chip: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-600',
    },
  ];

  const functions = [
    { icon: Landmark, name: 'Finance & Accounts', chip: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-600', hover: 'hover:border-emerald-400/50' },
    { icon: Megaphone, name: 'Marketing & Growth', chip: 'bg-amber-500/10 border-amber-500/25 text-amber-600', hover: 'hover:border-amber-400/50' },
    { icon: HeartHandshake, name: 'HR & People', chip: 'bg-rose-500/10 border-rose-500/25 text-rose-600', hover: 'hover:border-rose-400/50' },
    { icon: Cpu, name: 'Technology & Product', chip: 'bg-blue-500/10 border-blue-500/25 text-blue-600', hover: 'hover:border-blue-400/50' },
    { icon: TrendingUp, name: 'Sales & Revenue', chip: 'bg-violet-500/10 border-violet-500/25 text-violet-600', hover: 'hover:border-violet-400/50' },
    { icon: Scale, name: 'Legal & Compliance', chip: 'bg-cyan-500/10 border-cyan-500/25 text-cyan-600', hover: 'hover:border-cyan-400/50' },
  ];

  const faqs = [
    {
      q: 'What exactly is a fractional hire?',
      a: 'A fractional leader is an experienced senior professional — say a CFO or CMO — who works with your company part-time, typically one to three days a week, for an agreed duration. They own the function like an employee would, without the cost and commitment of a full-time senior package.',
    },
    {
      q: 'How is this different from hiring a consultant?',
      a: 'A consultant advises on a scoped problem and leaves. A fractional leader is embedded in your team on a recurring schedule — they run the function, build systems and processes, mentor your team, and stay accountable for outcomes over months, not deliverables.',
    },
    {
      q: 'How do you vet fractional experts?',
      a: 'Every expert is assessed before an introduction: structured AI screening on their domain, functional and role-based assessments on the SearchJob.ai platform, and a written evaluation report — reviewed by our team, so no one reaches you on machine judgment alone.',
    },
    {
      q: 'What does a fractional engagement cost?',
      a: 'Engagements are typically priced as a monthly retainer for an agreed number of days per week, or a day rate for shorter needs. Because you pay only for the time you need, the cost is a fraction of an equivalent full-time senior hire. Share the role and we will send a clear, written proposal.',
    },
    {
      q: 'What if we later want them full-time?',
      a: 'That happens often, and it is a good outcome — a fractional engagement is the best trial period there is. If both sides want it, we help you convert the engagement into a permanent hire.',
    },
    {
      q: 'What if the fit is not right?',
      a: 'Fractional engagements are designed to be low-risk: short notice periods, flexible terms, and our team stays involved after the start. If the fit is wrong, we work with you to replace the expert quickly.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        {/* ═════════════ HERO (split) ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-14 items-center">
              <div className="text-center lg:text-left pb-12 lg:pb-16">
                {/* <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-5">
                  <Sparkles className="w-3.5 h-3.5" />
                  Fractional hiring
                </div> */}
              
                <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] xl:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.06] mb-6">
                  Hire a world-class <RotatingRole />
                  <br />
                  <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    to grow your startup.
                  </span>
                </h1>
                <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8">
                  Proven executives who have built, scaled and led
                  before engaged on terms that fit your stage. Every
                  leader is rigorously assessed on the SearchJob.ai
                  platform and personally reviewed by our team before an
                  introduction.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <Button
                    size="lg"
                    onClick={() => navigate('/hirewithus/contact')}
                    className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
                  >
                    Hire an Expert
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/hirewithus/contact')}
                    className="h-12 px-8 text-base font-semibold !rounded-full bg-card/70 backdrop-blur-md"
                  >
                    Join as an Expert
                  </Button>
                </div>

                {/* <div className="mt-7 flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm text-muted-foreground">
                  {[
                    { icon: ShieldCheck, t: 'Vetted experts' },
                    { icon: Zap, t: 'Flexible engagements' },
                    { icon: RefreshCcw, t: 'No long-term lock-in' },
                  ].map(({ icon: Icon, t }) => (
                    <span key={t} className="inline-flex items-center gap-1.5 font-medium">
                      <Icon className="w-4 h-4 text-primary" />
                      {t}
                    </span>
                  ))}
                </div> */}

                {/* <div className="mt-8 pt-6 border-t border-border/60">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                    Trusted by growing companies
                  </p>
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-3">
                    {['/kinzy.png', '/grandgaming.png', '/idyaite.png', '/creowis.webp', '/manest1.png'].map((l) => (
                      <img
                        key={l}
                        src={l}
                        alt="Client logo"
                        className="h-6 w-auto object-contain opacity-80"
                      />
                    ))}
                  </div>
                </div> */}
                {/* <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-muted-foreground">
                  <TrustDot text="CXOs, VPs & specialists" />
                  <TrustDot text="Flexible days per week" />
                  <TrustDot text="Assessed before you meet them" />
                </div> */}
              </div>

              {/* Hero image — layered composition: cutout + floating cards + notes */}
              <div className="relative flex justify-center lg:justify-end self-end w-full max-w-[640px] mx-auto lg:mx-0 lg:ml-auto pt-6 lg:pr-6">
                {/* single soft circle + thin ring backdrop */}
                <div
                  className="absolute left-[62%] top-[54%] -translate-x-1/2 -translate-y-1/2 w-[80%] aspect-square rounded-full bg-gradient-to-br from-emerald-200/70 via-teal-100/60 to-emerald-100/40 -z-10"
                  aria-hidden="true"
                />
                <div
                  className="absolute left-[62%] top-[50%] -translate-x-1/2 -translate-y-1/2 w-[92%] aspect-square rounded-full border border-emerald-400/40 -z-10"
                  aria-hidden="true"
                />

                <img
                  src="/fractional-hero-cut.png"
                  alt="Fractional leader — senior expertise engaged a few days a week"
                  className="w-full max-w-[460px] h-auto drop-shadow-2xl relative z-10"
                />

                {/* sample expert profile card */}
                <div className="hidden lg:block absolute left-0 top-8 z-20 w-48 rounded-2xl bg-card border border-border/60 shadow-2xl p-3.5 fractional-float">
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Available
                    </span>
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted border border-border/60 rounded-full px-2 py-0.5">
                      Sample
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground leading-tight">Meher S.</h3>
                  <p className="text-xs font-semibold text-primary mb-1">Fractional CMO</p>
                  <p className="flex items-center gap-1 text-[11px] text-muted-foreground mb-2.5 whitespace-nowrap">
                    <BadgeCheck className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span className="font-bold text-foreground">Strong evidence</span>
                    <span>(assessed)</span>
                  </p>
                  <div className="space-y-1.5 text-[11px] text-muted-foreground mb-3 whitespace-nowrap">
                    <span className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-foreground/60 shrink-0" />15+ years experience</span>
                    <span className="flex items-center gap-2"><Mic className="w-3.5 h-3.5 text-foreground/60 shrink-0" />AI screened &amp; reviewed</span>
                    <span className="flex items-center gap-2"><Tag className="w-3.5 h-3.5 text-foreground/60 shrink-0" />B2B SaaS · Growth · GTM</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/hirewithus/contact')}
                    className="rounded-full bg-primary text-primary-foreground text-[11px] font-bold px-3.5 py-1.5 inline-flex items-center gap-1.5 hover:bg-primary/90 transition-colors shadow-md shadow-primary/25"
                  >
                    View Profile
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {/* evaluation chart card */}
                <div className="hidden lg:block absolute -right-24 top-[30%] z-20 w-48 rounded-3xl bg-card border border-border/60 shadow-2xl p-4 fractional-float-delay">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-sm font-bold text-foreground">Evaluation report</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      Strong
                      <TrendingUp className="w-3 h-3" />
                    </span>
                  </div>
                  <div className="flex items-end gap-1.5 h-16 mb-3">
                    {[
                      { h: 35, c: 'bg-gradient-to-t from-sky-500 to-sky-300' },
                      { h: 45, c: 'bg-gradient-to-t from-violet-500 to-violet-300' },
                      { h: 55, c: 'bg-gradient-to-t from-amber-500 to-amber-300' },
                      { h: 62, c: 'bg-gradient-to-t from-rose-500 to-rose-300' },
                      { h: 74, c: 'bg-gradient-to-t from-teal-500 to-teal-300' },
                      { h: 88, c: 'bg-gradient-to-t from-primary to-emerald-400' },
                    ].map(({ h, c }, i) => (
                      <span
                        key={i}
                        style={{ height: `${h}%` }}
                        className={`flex-1 rounded-t-md ${c}`}
                      />
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Evidence you can read before any introduction
                  </p>
                </div>

                {/* verified checklist card */}
                <div className="hidden md:block absolute left-[2%] bottom-[7%] z-20 rounded-2xl bg-card border border-border/60 shadow-2xl px-4 py-3.5 space-y-2 fractional-float-slow">
                  {[
                    { t: 'AI screened' },
                    { t: 'Leadership assessment' },
                    { t: 'Personally reviewed' },
                  ].map(({ t }) => (
                    <span key={t} className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                      <span className="w-5 h-5 rounded-full bg-gradient-to-br from-primary to-emerald-600 text-white flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-3 h-3" />
                      </span>
                      {t}
                    </span>
                  ))}
                </div>

                {/* handwritten annotations */}
                <div className="hidden xl:block absolute right-0 top-0 z-20" aria-hidden="true">
                  <p className="font-hand text-3xl leading-[1.02] text-foreground/85 rotate-[6deg] text-left">
                    Fractional
                    <br />
                    Leadership
                    <br />
                    Real Impact
                  </p>
                  <svg viewBox="0 0 70 46" className="w-14 h-9 -ml-6 mt-1 text-foreground/50" fill="none">
                    <path d="M60 6 C46 14, 30 22, 12 36" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M22 30 L10 38 L24 40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </div>
                <div className="hidden xl:block absolute -right-10 bottom-[16%] z-30 text-left" aria-hidden="true">
                  <svg viewBox="0 0 70 46" className="w-14 h-9 mb-1 text-foreground/50" fill="none">
                    <path d="M64 40 C48 32, 32 22, 14 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M16 22 L12 8 L28 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  <p className="font-hand text-3xl leading-[1.02] text-foreground/85 -rotate-[8deg]">
                    Top Talent
                    <br />
                    On Your Terms
                  </p>
                </div>

                {/* role chips */}
                <div className="hidden md:flex absolute bottom-1 left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0 lg:right-0 z-20 items-center gap-1 rounded-full bg-card border border-border/60 shadow-2xl p-1.5">
                  {['CMO', 'CTO', 'CFO', 'COO', 'CHRO'].map((r, i) => (
                    <span
                      key={r}
                      className={`rounded-full px-3 py-1.5 text-xs font-bold ${
                        i === 0
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'text-foreground/70'
                      }`}
                    >
                      {r}
                    </span>
                  ))}
                  <span className="rounded-full px-3 py-1.5 text-xs font-bold text-primary">More +</span>
                </div>

                <style>{`
                  @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap');
                  .font-hand { font-family: 'Caveat', cursive; }
                  .fractional-blob {
                    border-radius: 58% 42% 38% 62% / 55% 40% 60% 45%;
                  }
                  .fractional-blob-2 {
                    border-radius: 42% 58% 62% 38% / 45% 60% 40% 55%;
                  }
                  .fractional-photo-blob {
                    border-radius: 58% 42% 44% 56% / 50% 44% 56% 50%;
                  }
                  .fractional-photo-blob-2 {
                    border-radius: 44% 56% 58% 42% / 56% 48% 52% 44%;
                  }
                  @keyframes fractional-float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                  }
                  .fractional-float { animation: fractional-float 5s ease-in-out infinite; }
                  .fractional-float-delay { animation: fractional-float 6s ease-in-out 1.2s infinite; }
                  .fractional-float-slow { animation: fractional-float 7s ease-in-out 0.6s infinite; }
                  @media (prefers-reduced-motion: reduce) {
                    .fractional-float, .fractional-float-delay, .fractional-float-slow { animation: none; }
                  }
                `}</style>
              </div>
            </div>
          </div>
        </section>

        {/* ═════════════ TRUST BAR — running expert marquee ═════════════ */}
        <section className="relative py-10 lg:py-12 bg-muted/30 border-y border-border/60 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-8">
              Assessed fractional leaders, across every function
            </p>
          </div>

          <div
            className="relative group/logos"
            style={{
              maskImage:
                'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
            }}
          >
            <div className="flex w-max fractional-logo-track">
              {[0, 1].map((copy) => (
                <div
                  key={copy}
                  aria-hidden={copy === 1}
                  className="flex items-center gap-4 sm:gap-6 pr-4 sm:pr-6"
                >
                  {[...marqueeExperts, ...marqueeExperts].map((e, idx) => (
                    <div
                      key={`${copy}-${idx}-${e.role}`}
                      className="flex flex-col items-center gap-2 shrink-0 select-none px-2 group/expert"
                    >
                      <span
                        className={`relative rounded-full p-[3px] bg-gradient-to-br ${
                          ['from-primary to-emerald-600', 'from-amber-400 to-amber-500', 'from-teal-400 to-cyan-500'][idx % 3]
                        } shadow-md group-hover/expert:scale-105 transition-transform duration-300`}
                      >
                        <span className="block w-16 h-16 rounded-full overflow-hidden border-2 border-white">
                          <img
                            src={e.img}
                            alt={e.role}
                            draggable="false"
                            className="w-full h-full object-cover"
                          />
                        </span>
                        <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-primary to-emerald-600 border-2 border-white text-white flex items-center justify-center">
                          <BadgeCheck className="w-3 h-3" />
                        </span>
                      </span>
                      <span className="rounded-full border border-border/70 bg-card shadow-sm px-2.5 py-0.5 text-[11px] font-bold text-foreground whitespace-nowrap">
                        {e.role}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <style>{`
            @keyframes fractional-logo-scroll {
              from { transform: translateX(0); }
              to   { transform: translateX(-50%); }
            }
            .fractional-logo-track {
              animation: fractional-logo-scroll 32s linear infinite;
            }
            .group\\/logos:hover .fractional-logo-track {
              animation-play-state: paused;
            }
            @media (prefers-reduced-motion: reduce) {
              .fractional-logo-track { animation: none; }
            }
          `}</style>
        </section>

        {/* ═════════════ YOUR LEADERSHIP BENCH (dark band) ═════════════ */}
        <section
          className="relative pt-14 lg:pt-16 pb-10 lg:pb-12 overflow-hidden text-white"
          style={{ backgroundColor: INK }}
        >
          <div className="absolute top-[-25%] left-[-10%] w-[55%] h-[70%] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-[-30%] right-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                {/* <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                  Your leadership bench
                </div> */}
                <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.1] mb-5">
                  The leaders you need,{' '}
                  <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    on your terms.
                  </span>
                </h2>
                <p className="text-lg text-white/70 leading-relaxed mb-6">
                  CFOs, CMOs, CHROs and functional heads who have done it
                  before at your table without the full-time
                  commitment. One conversation, and the right leader is
                  working on your business.
                </p>
                <ul className="space-y-3">
                  {[
                    'Hand-picked for your stage and industry',
                    'Engaged in days, not hiring cycles',
                    'Scale from one leader to a full bench',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-400/15 flex items-center justify-center mt-0.5">
                        <CheckCircle2
                          className="w-3.5 h-3.5 text-emerald-300"
                          strokeWidth={2.5}
                        />
                      </span>
                      <span className="text-base text-white/85 leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <BlobPanel>
                <ExpertCollage />
              </BlobPanel>
            </div>
          </div>
        </section>

        {/* ═════════════ WHO IT'S FOR ═════════════ */}
        <Section muted>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Is it right for you?</Eyebrow>
            <SectionHeading
              center
              plain="Fractional works best when"
              accent="the timing sounds like this."
            />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {fitCards.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.n}
                  className="relative bg-card border border-border/70 rounded-3xl p-6 lg:p-7 shadow-sm hover:-translate-y-1 hover:shadow-[0_16px_48px_-16px_hsl(var(--primary)/0.25)] transition-all duration-300"
                >
                  <span
                    className={`text-4xl font-black bg-gradient-to-br ${c.num} bg-clip-text text-transparent leading-none`}
                  >
                    {c.n}
                  </span>
                  <div
                    className={`w-10 h-10 rounded-xl border flex items-center justify-center my-3 ${c.chip}`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                    {c.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ ROLES WE COVER ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Roles we cover</Eyebrow>
            <SectionHeading
              center
              plain="Fractional talent at"
              accent="every senior level."
            />
            <p className="text-lg text-muted-foreground leading-relaxed">
              From the corner office to deep specialists — engaged for the
              days per week the work actually needs.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {roleFamilies.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.title}
                  className="group bg-card border border-border/70 rounded-3xl p-6 lg:p-7 shadow-sm hover:shadow-[0_18px_60px_-18px_hsl(var(--primary)/0.28)] hover:-translate-y-1 transition-all duration-300"
                >
                  <div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${r.iconGrad} text-white flex items-center justify-center shadow-lg ${r.shadow} mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2 leading-snug">
                    {r.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {r.desc}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.roles.map((t) => (
                      <span
                        key={t}
                        className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${r.tag}`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Function chips */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            {functions.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.name}
                  className={`group flex flex-col items-center text-center gap-3 rounded-3xl border border-border/70 bg-card px-4 py-6 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${f.hover}`}
                >
                  <span
                    className={`w-12 h-12 rounded-2xl border flex items-center justify-center group-hover:scale-110 transition-transform duration-300 ${f.chip}`}
                  >
                    <Icon className="w-6 h-6" />
                  </span>
                  <span className="text-sm font-semibold text-foreground leading-snug">
                    {f.name}
                  </span>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ VETTING — the differentiator (dark band) ═════════════ */}
        <section
          className="relative pt-14 lg:pt-16 pb-10 lg:pb-12 overflow-hidden text-white"
          style={{ backgroundColor: INK }}
        >
          <div className="absolute top-[-25%] right-[-10%] w-[55%] h-[70%] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              {/* <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                How we vet
              </div> */}
              <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.1] mb-5">
                Assessed on our platform,{' '}
                <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                  not just listed.
                </span>
              </h2>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                Anyone can claim &ldquo;fractional CFO&rdquo;. On
                SearchJob.ai, every expert proves it — evidence first,
                introductions after.
              </p>
              <div className="space-y-4 max-w-md mx-auto lg:mx-0">
                {[
                  {
                    icon: Mic,
                    title: 'Evidence, not resumes',
                    sub: 'AI screening on their real domain',
                    grad: 'from-emerald-500 to-teal-600',
                    shadow: 'shadow-emerald-500/30',
                  },
                  {
                    icon: FileCheck,
                    title: 'A report you can read first',
                    sub: 'written evaluation before any intro',
                    grad: 'from-primary to-emerald-700',
                    shadow: 'shadow-primary/30',
                  },
                  {
                    icon: UserCheck,
                    title: 'Humans make the final call',
                    sub: 'never machine judgment alone',
                    grad: 'from-teal-500 to-cyan-600',
                    shadow: 'shadow-teal-500/30',
                  },
                ].map((f) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={f.title}
                      className="flex items-center gap-4 rounded-2xl border border-white/10 bg-card px-4 py-3.5 shadow-lg hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_hsl(var(--primary)/0.3)] transition-all duration-300"
                    >
                      <span
                        className={`shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${f.grad} text-white flex items-center justify-center shadow-lg ${f.shadow}`}
                      >
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="min-w-0 text-left">
                        <span className="block text-sm font-bold text-foreground leading-snug">
                          {f.title}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {f.sub}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <BlobPanel flip>
              <VettingJourney />
            </BlobPanel>
          </div>
          </div>
        </section>

        {/* ═════════════ FRACTIONAL vs FULL-TIME vs CONSULTANT ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Know the difference</Eyebrow>
            <SectionHeading
              center
              plain="Fractional, full-time"
              accent="or consultant?"
            />
          </div>

          <div className="overflow-x-auto rounded-3xl border border-border/70 shadow-sm max-w-5xl mx-auto">
            <table className="w-full min-w-[720px] text-left bg-card">
              <thead>
                <tr className="border-b border-border/70">
                  <th className="p-4 lg:p-5 text-sm font-semibold text-muted-foreground w-[16%]"> </th>
                  <th className="p-4 lg:p-5 w-[28%]">
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-primary">
                      <Sparkles className="w-4 h-4" />
                      Fractional leader
                    </span>
                  </th>
                  <th className="p-4 lg:p-5 text-sm font-bold text-foreground w-[28%]">
                    Full-time hire
                  </th>
                  <th className="p-4 lg:p-5 text-sm font-bold text-foreground w-[28%]">
                    Consultant
                  </th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row, i) => (
                  <tr
                    key={row.label}
                    className={i < compareRows.length - 1 ? 'border-b border-border/50' : ''}
                  >
                    <td className="p-4 lg:p-5 text-sm font-semibold text-foreground align-top">
                      {row.label}
                    </td>
                    <td className="p-4 lg:p-5 text-sm text-foreground/90 align-top bg-primary/[0.04]">
                      {row.fractional}
                    </td>
                    <td className="p-4 lg:p-5 text-sm text-muted-foreground align-top">
                      {row.fullTime}
                    </td>
                    <td className="p-4 lg:p-5 text-sm text-muted-foreground align-top">
                      {row.consultant}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* ═════════════ HOW IT WORKS ═════════════ */}
        <Section muted>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>How it works</Eyebrow>
            <SectionHeading
              center
              plain="From first call to first day,"
              accent="in four clear steps."
            />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((st) => {
              const Icon = st.icon;
              return (
                <div
                  key={st.n}
                  className="relative bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-[0_16px_48px_-16px_hsl(var(--primary)/0.25)] transition-all duration-300"
                >
                  <span
                    className={`text-4xl font-black bg-gradient-to-br ${st.num} bg-clip-text text-transparent leading-none`}
                  >
                    {st.n}
                  </span>
                  <div
                    className={`w-10 h-10 rounded-xl border flex items-center justify-center my-3 ${st.chip}`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                    {st.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {st.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ FOR EXPERTS (dark band) ═════════════ */}
        <section
          className="relative py-14 lg:py-20 overflow-hidden text-white"
          style={{ backgroundColor: INK }}
        >
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-2/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                  For senior professionals
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                  Ready to work{' '}
                  <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    on your terms?
                  </span>
                </h2>
                <p className="text-lg text-white/70 leading-relaxed max-w-2xl">
                  If you are an experienced CXO, VP or functional expert who
                  wants meaningful work without full-time commitment — join
                  as a fractional expert. Complete our assessment once, and
                  we bring engagements to you.
                </p>
              </div>
              <div className="flex lg:justify-end">
                <Button
                  size="lg"
                  onClick={() => navigate('/hirewithus/contact')}
                  className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                >
                  Join as an Expert
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ═════════════ FAQ ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>FAQ</Eyebrow>
            <SectionHeading
              center
              plain="Questions founders ask"
              accent="before going fractional."
            />
          </div>
          <div className="space-y-4 max-w-3xl mx-auto">
            {faqs.map((f) => (
              <FAQItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </Section>

        {/* ═════════════ FINAL CTA (dark) ═════════════ */}
        <section className="relative py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl"
              style={{ backgroundColor: INK }}
            >
              <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
                    Need senior expertise?
                    <br />
                    <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                      Go fractional with us.
                    </span>
                  </h2>
                  <p className="text-lg text-white/70 leading-relaxed mb-8">
                    Tell us the function and the outcomes you need — we will
                    come back with assessed, evidence-backed fractional
                    leaders ready to start on your terms.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      size="lg"
                      onClick={() => navigate('/hirewithus/contact')}
                      className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                    >
                      Hire an Expert
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => navigate('/hirewithus/staffing')}
                      className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
                    >
                      All staffing services
                    </Button>
                  </div>
                </div>

                <div className="lg:pl-8 lg:border-l lg:border-white/10 space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                    Talk to our team
                  </p>
                  <DarkContactLine icon={MapPin} text="Bangalore, India" />
                  <DarkContactLine icon={Mail} text="contact@searchjob.ai" />
                  <DarkContactLine icon={Globe} text="www.searchjob.ai" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Local building blocks — mirror the landing / AboutUs page        */
/* ─────────────────────────────────────────────────────────────── */
function Section({ children, muted = false, id, decor = false }) {
  return (
    <section
      id={id}
      className={`relative py-14 lg:py-20 overflow-hidden ${
        muted ? 'bg-muted/30' : 'bg-background'
      }`}
    >
      {decor && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {/* gentle mint wash so the section doesn't read flat white */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/70 via-transparent to-teal-50/60" />
          {/* soft brand glows in the corners */}
          <div className="absolute -top-28 -left-28 w-[26rem] h-[26rem] bg-primary/15 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-28 w-[30rem] h-[30rem] bg-emerald-500/15 rounded-full blur-3xl" />
          <div className="absolute top-1/4 right-[8%] w-72 h-72 bg-teal-400/15 rounded-full blur-3xl" />
          {/* faint dot grid, faded toward the middle */}
          <div
            className="absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                'radial-gradient(circle, hsl(var(--primary) / 0.35) 1px, transparent 1px)',
              backgroundSize: '26px 26px',
              maskImage:
                'radial-gradient(ellipse 90% 90% at 50% 50%, transparent 35%, black 100%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 90% 90% at 50% 50%, transparent 35%, black 100%)',
            }}
          />
        </div>
      )}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, center = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4 ${
        center ? 'mx-auto' : ''
      }`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

function SectionHeading({ plain, accent, center = false }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.1] mb-5 ${
        center ? 'text-center' : ''
      }`}
    >
      {plain}{' '}
      <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        {accent}
      </span>
    </h2>
  );
}

function CheckList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
          </span>
          <span className="text-base text-foreground/85 leading-relaxed">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function BlobPanel({ children, className = '', flip = false, chips = [] }) {
  return (
    <div
      className={`relative flex items-center justify-center min-h-[320px] lg:min-h-[360px] py-8 px-8 ${className}`}
    >
      {/* big solid brand blob — same treatment as the hero */}
      <div
        className={`absolute inset-y-0 inset-x-2 -z-10 bg-gradient-to-tr from-primary/35 via-emerald-400/30 to-teal-300/35 blur-2xl ${
          flip ? 'fractional-blob-2' : 'fractional-blob'
        }`}
        aria-hidden="true"
      />
      <div
        className={`absolute w-[85%] h-[85%] top-1/2 -translate-y-1/2 -z-10 bg-gradient-to-br from-emerald-300/40 to-teal-200/35 ${
          flip ? '-left-2 fractional-blob' : '-right-2 fractional-blob-2'
        }`}
        aria-hidden="true"
      />
      {/* dashed accent ring + dot */}
      <div
        className={`absolute -z-10 w-36 h-36 rounded-full border-2 border-dashed border-primary/30 ${
          flip ? 'right-4 top-4' : 'left-4 top-4'
        }`}
        aria-hidden="true"
      />
      <div
        className={`absolute w-4 h-4 rounded-full bg-gradient-to-br from-primary to-emerald-600 shadow-md ${
          flip ? 'left-10 bottom-12' : 'right-10 bottom-12'
        }`}
        aria-hidden="true"
      />
      <div className="relative w-full">{children}</div>
      {chips.map((c, i) => (
        <HeroChip
          key={c.title}
          icon={c.icon}
          title={c.title}
          sub={c.sub}
          className={`${c.className} ${i % 2 ? 'fractional-float-delay' : 'fractional-float'}`}
        />
      ))}
    </div>
  );
}

function ExpertCollage() {
  const bubbles = [
    {
      src: '/expert-cmo.png',
      role: 'Fractional CMO',
      wrap: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[50%] z-20',
      ringBg: 'bg-gradient-to-br from-primary/90 to-emerald-600/90',
      float: '',
    },
    {
      src: '/expert-cfo.png',
      role: 'Fractional CFO',
      wrap: 'absolute left-[2%] top-[4%] w-[36%] z-10 fractional-float',
      ringBg: 'bg-gradient-to-br from-amber-400/90 to-amber-500/90',
      float: '',
    },
    {
      src: '/expert-chro.png',
      role: 'Fractional CHRO',
      wrap: 'absolute right-[2%] bottom-[6%] w-[34%] z-30 fractional-float-delay',
      ringBg: 'bg-gradient-to-br from-teal-400/90 to-cyan-500/90',
      float: '',
    },
  ];
  return (
    <div className="relative w-full max-w-[420px] mx-auto aspect-square">
      {/* doodles */}
      <svg
        viewBox="0 0 100 100"
        className="absolute -top-3 right-[10%] w-24 h-24 text-amber-400/80"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 55 C10 30, 40 12, 62 16 C86 21, 94 45, 84 64 C73 84, 40 92, 24 78 C12 68, 10 58, 14 48"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
      <svg
        viewBox="0 0 120 80"
        className="absolute bottom-[2%] -left-3 w-28 h-20 text-teal-500/60"
        aria-hidden="true"
      >
        {[0, 1, 2].map((r) =>
          [0, 1, 2, 3].map((c) => (
            <g
              key={`${r}-${c}`}
              transform={`translate(${12 + c * 28}, ${12 + r * 26})`}
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            >
              <line x1="-5" y1="-5" x2="5" y2="5" />
              <line x1="-5" y1="5" x2="5" y2="-5" />
            </g>
          ))
        )}
      </svg>
      <svg
        viewBox="0 0 100 100"
        className="absolute top-[30%] -right-4 w-16 h-16 text-primary/70"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M15 80 C40 75, 70 55, 78 25"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M66 30 L79 22 L81 37"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      <svg
        viewBox="0 0 40 40"
        className="absolute top-[6%] left-[38%] w-8 h-8 text-emerald-500/80"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M20 2 C22 12, 28 18, 38 20 C28 22, 22 28, 20 38 C18 28, 12 22, 2 20 C12 18, 18 12, 20 2 Z" />
      </svg>

      {/* expert bubbles */}
      {bubbles.map((b) => (
        <div key={b.role} className={b.wrap}>
          <div className={`rounded-full p-1.5 ${b.ringBg} shadow-xl`}>
            <span className="block rounded-full overflow-hidden border-4 border-white aspect-square">
              <img
                src={b.src}
                alt={b.role}
                className="w-full h-full object-cover"
              />
            </span>
          </div>
          <span className="absolute left-1/2 -translate-x-1/2 -bottom-3 whitespace-nowrap rounded-full bg-card border border-border/70 shadow-md px-3 py-1 text-[11px] font-bold text-foreground">
            {b.role}
          </span>
        </div>
      ))}
    </div>
  );
}

function WeekStrip() {
  const days = [
    { d: 'Mon', on: false },
    { d: 'Tue', on: true },
    { d: 'Wed', on: false },
    { d: 'Thu', on: true },
    { d: 'Fri', on: false },
  ];
  return (
    <div className="mt-6 mx-auto max-w-[460px] rounded-3xl border border-border/70 bg-card/80 backdrop-blur-md shadow-lg shadow-primary/10 p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-sm font-bold text-foreground inline-flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-primary" />
          Your week, your call
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 rounded-full px-2.5 py-1">
          2 of 5 days
        </span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {days.map(({ d, on }) => (
          <div
            key={d}
            className={
              on
                ? 'rounded-xl py-2.5 text-center text-xs font-bold text-white bg-gradient-to-br from-primary to-emerald-700 shadow-md shadow-primary/30'
                : 'rounded-xl py-2.5 text-center text-xs font-semibold text-muted-foreground bg-muted/60 border border-border/60'
            }
          >
            {d}
            {on ? (
              <CheckCircle2 className="w-3.5 h-3.5 mx-auto mt-1" />
            ) : (
              <span className="block w-3.5 h-3.5 mx-auto mt-1" />
            )}
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
        Pay only for the days your leader works — and change the mix any
        month.
      </p>
    </div>
  );
}

const ROTATING_ROLES = ['CFO', 'CMO', 'CTO', 'CHRO', 'COO'];

function RotatingRole() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }
    const id = setInterval(
      () => setIndex((i) => (i + 1) % ROTATING_ROLES.length),
      2200
    );
    return () => clearInterval(id);
  }, []);

  return (
    <span className="relative inline-block align-baseline min-w-[3.2ch] text-left">
      <span
        key={ROTATING_ROLES[index]}
        className="rotating-role bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent"
      >
        {ROTATING_ROLES[index]}
      </span>
      <style>{`
        .rotating-role {
          display: inline-block;
          animation: role-in 0.45s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes role-in {
          from { opacity: 0; transform: translateY(0.35em); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .rotating-role { animation: none; }
        }
      `}</style>
    </span>
  );
}

const JOURNEY_STEPS = [
  {
    icon: Mic,
    title: 'AI screening',
    sub: 'domain-tested, not resume-read',
    grad: 'from-emerald-500 to-teal-600',
    shadow: 'shadow-emerald-500/40',
  },
  {
    icon: ClipboardCheck,
    title: 'Functional assessment',
    sub: 'role-based skills, proven',
    grad: 'from-primary to-emerald-700',
    shadow: 'shadow-primary/40',
  },
  {
    icon: FileCheck,
    title: 'Evaluation report',
    sub: 'written evidence you can read',
    grad: 'from-teal-500 to-cyan-600',
    shadow: 'shadow-teal-500/40',
  },
  {
    icon: UserCheck,
    title: 'Human review',
    sub: 'our team makes the final call',
    grad: 'from-emerald-600 to-primary',
    shadow: 'shadow-emerald-600/40',
  },
  {
    icon: ShieldCheck,
    title: 'Verified expert',
    sub: 'ready to meet you',
    grad: 'from-amber-400 to-amber-500',
    shadow: 'shadow-amber-400/50',
    final: true,
  },
];

function VettingJourney() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    // Safety net: never leave the card invisible if the observer is
    // throttled or unavailable (e.g. hidden webviews).
    const fallback = setTimeout(() => setVisible(true), 3000);
    return () => {
      obs.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`vet-journey mx-auto max-w-[420px] p-2 ${
        visible ? 'vet-visible' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-5">
        <span className="text-sm font-bold text-white inline-flex items-center gap-2">
          <BadgeCheck className="w-4 h-4 text-emerald-300" />
          The 5-step vetting journey
        </span>
       
      </div>

      <div className="relative">
        {/* dashed rail + traveling dot */}
        <span
          className="absolute left-[25px] top-7 bottom-7 border-l-2 border-dashed border-emerald-300/30"
          aria-hidden="true"
        />
        <span className="vet-dot" aria-hidden="true" />

        <ol className="relative space-y-5 list-none m-0 p-0">
          {JOURNEY_STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <li
                key={s.title}
                className="vet-step flex items-center gap-4"
                style={{ '--i': i }}
              >
                <span
                  className={`vet-icon shrink-0 w-[52px] h-[52px] rounded-full bg-gradient-to-br ${s.grad} text-white flex items-center justify-center shadow-lg ${s.shadow} border-[3px] border-white`}
                >
                  <Icon className="w-6 h-6" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-white leading-snug">
                    {s.title}
                  </span>
                  <span className="block text-xs text-white/60">
                    {s.sub}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <style>{`
        .vet-journey:not(.vet-visible) .vet-step {
          opacity: 0;
          transform: translateX(-14px);
        }
        .vet-visible .vet-step {
          opacity: 1;
          transform: none;
          animation: vet-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) backwards;
          animation-delay: calc(var(--i) * 0.16s + 0.15s);
        }
        @keyframes vet-in {
          from { opacity: 0; transform: translateX(-14px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .vet-visible .vet-icon {
          animation: vet-pulse 8s ease-in-out infinite;
          animation-delay: calc(var(--i) * 1.6s + 1.2s);
        }
        @keyframes vet-pulse {
          0%, 14%, 100% { transform: scale(1); }
          6% { transform: scale(1.14); }
        }
        .vet-dot {
          position: absolute;
          left: 20px;
          top: 7%;
          width: 12px;
          height: 12px;
          border-radius: 9999px;
          background: linear-gradient(135deg, hsl(var(--primary)), #10b981);
          box-shadow: 0 0 0 4px hsl(var(--primary) / 0.15), 0 0 12px hsl(var(--primary) / 0.5);
          opacity: 0;
          z-index: 1;
        }
        .vet-visible .vet-dot {
          animation: vet-travel 8s ease-in-out infinite;
          animation-delay: 1.2s;
        }
        @keyframes vet-travel {
          0% { top: 6%; opacity: 0; }
          4% { opacity: 1; }
          20% { top: 27%; }
          40% { top: 48%; }
          60% { top: 69%; }
          78% { top: 88%; opacity: 1; }
          84% { top: 88%; opacity: 0; }
          100% { top: 88%; opacity: 0; }
        }
        .vet-visible .vet-final-check {
          animation: vet-pulse 8s ease-in-out infinite;
          animation-delay: 7.6s;
        }
        @media (prefers-reduced-motion: reduce) {
          .vet-step { opacity: 1; transform: none; animation: none !important; }
          .vet-icon, .vet-dot, .vet-final-check { animation: none !important; }
          .vet-dot { display: none; }
        }
      `}</style>
    </div>
  );
}

function ScoreCard() {
  const rows = [
    { label: 'Domain depth', level: 'Strong evidence', w: '88%', grad: 'from-emerald-500 to-teal-500' },
    { label: 'Case response', level: 'Structured', w: '76%', grad: 'from-primary to-emerald-600' },
    { label: 'Communication', level: 'Clear & concise', w: '82%', grad: 'from-teal-500 to-cyan-500' },
  ];
  return (
    <div className="mt-6 mx-auto max-w-[460px] rounded-3xl border border-border/70 bg-card/80 backdrop-blur-md shadow-lg shadow-primary/10 p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <span className="text-sm font-bold text-foreground inline-flex items-center gap-2">
          <BadgeCheck className="w-4 h-4 text-primary" />
          Expert evaluation — Fractional CFO
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted border border-border/60 rounded-full px-2.5 py-1">
          Sample
        </span>
      </div>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-foreground">
                {r.label}
              </span>
              <span className="text-[11px] font-medium text-primary">
                {r.level}
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${r.grad}`}
                style={{ width: r.w }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-border/60 flex items-center gap-2 text-xs text-muted-foreground">
        <UserCheck className="w-4 h-4 text-primary shrink-0" />
        Final call always made by the SearchJob.ai team — never AI alone.
      </div>
    </div>
  );
}

function HeroChip({ icon: Icon, title, sub, className = '' }) {
  return (
    <div
      className={`absolute z-10 flex items-center gap-2.5 rounded-2xl border border-white/60 bg-white/70 backdrop-blur-md px-3.5 py-2.5 shadow-lg shadow-primary/10 ${className}`}
    >
      <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-white flex items-center justify-center shrink-0 shadow-md">
        <Icon className="w-5 h-5" />
      </span>
      <span className="leading-tight">
        <span className="block text-sm font-bold text-foreground">{title}</span>
        <span className="block text-[11px] font-medium text-muted-foreground">{sub}</span>
      </span>
    </div>
  );
}

function VetBadge({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3.5 py-2 text-sm font-semibold text-primary">
      <Icon className="w-4 h-4" />
      {text}
    </span>
  );
}

function TrustDot({ text }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <CheckCircle2 className="w-4 h-4 text-primary" />
      {text}
    </span>
  );
}

function DarkContactLine({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-emerald-300" />
      </span>
      <span className="text-sm font-medium text-white/90">{text}</span>
    </div>
  );
}

function FAQItem({ q, a }) {
  return (
    <details className="group bg-card border border-border/70 rounded-2xl p-5 shadow-sm open:shadow-md transition-shadow">
      <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
        <span className="text-base font-bold text-foreground leading-snug">
          {q}
        </span>
        <span className="shrink-0 w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary transition-transform duration-300 group-open:rotate-45">
          <span className="text-lg leading-none font-light">+</span>
        </span>
      </summary>
      <p className="text-sm text-muted-foreground leading-relaxed mt-3">
        {a}
      </p>
    </details>
  );
}
